import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import { DailyRecord } from '@/types/cbs';
import { useCBSDailyRecordStore } from '@/stores/cbsDailyRecordStore';
import { useCBSBatchStore } from '@/stores/cbsBatchStore';
import { getAuthenticatedClient } from '@/services/client';
import { edgeFunctions } from '@/services/edgeFunctions';
import { toast } from '@/hooks/use-toast';

// Fetch all records for a batch
export const useBatchRecords = (batchId: string) => {
  const { getRecordsForBatch } = useCBSDailyRecordStore();

  return useQuery({
    queryKey: ['cbs', 'dailyRecords', batchId],
    queryFn: async () => {
      try {
        const client = getAuthenticatedClient();
        const { data, error } = await client
          .from('daily_records')
          .select('*')
          .eq('batch_id', batchId)
          .order('batch_day', { ascending: false });
        if (error) throw error;
        return data ?? [];
      } catch {
        return getRecordsForBatch(batchId);
      }
    },
    staleTime: 1000 * 60 * 5,
    initialData: getRecordsForBatch(batchId),
    enabled: !!batchId,
  });
};

// Fetch single record
export const useDailyRecord = (recordId: string) => {
  return useQuery({
    queryKey: ['cbs', 'dailyRecord', recordId],
    queryFn: async () => {
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('daily_records')
        .select('*')
        .eq('id', recordId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!recordId,
  });
};

// Check if today's record exists
export const useTodayRecord = (batchId: string, date: string) => {
  const { getTodayRecord } = useCBSDailyRecordStore();

  return useQuery({
    queryKey: ['cbs', 'dailyRecord', 'today', batchId, date],
    queryFn: async () => {
      try {
        const client = getAuthenticatedClient();
        const { data, error } = await client
          .from('daily_records')
          .select('*')
          .eq('batch_id', batchId)
          .eq('record_date', date)
          .maybeSingle();
        if (error) throw error;
        return data ?? null;
      } catch {
        return getTodayRecord(batchId, date);
      }
    },
    staleTime: 1000 * 60 * 2,
    initialData: getTodayRecord(batchId, date),
    enabled: !!batchId && !!date,
  });
};

// Create daily record
export const useCreateDailyRecord = () => {
  const queryClient = useQueryClient();
  const { addRecord } = useCBSDailyRecordStore();
  const { updateBatch, activeBatch } = useCBSBatchStore();

  return useMutation({
    mutationFn: async (input: Omit<DailyRecord, 'id' | 'created_at' | 'updated_at' | 'running_bird_count' | 'running_cumulative_feed_kg' | 'running_fcr'>) => {
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from('daily_records')
        .insert(input)
        .select()
        .single();
      if (error) throw error;

      // Trigger server-side processing (running totals, FCR calculation, alerts)
      try {
        await edgeFunctions.processDailyRecord({
          batchId: input.batch_id,
          date: input.record_date,
          feedKg: input.feed_consumed_kg ?? 0,
          mortalityCount: input.mortality_count,
          waterLiters: input.water_consumed_liters ?? undefined,
          tempMin: input.temperature_min_c ?? undefined,
          tempMax: input.temperature_max_c ?? undefined,
          weightSamples: input.weight_sample_count && input.weight_sample_avg_g
            ? { count: input.weight_sample_count, avgGrams: input.weight_sample_avg_g }
            : undefined,
        });
      } catch (edgeError) {
        // Don't fail the mutation if edge function fails — data is already saved
        console.warn('Edge function processDailyRecord failed:', edgeError);
      }

      return data;
    },

    onMutate: async (newRecord) => {
      await queryClient.cancelQueries({ queryKey: ['cbs', 'dailyRecords', newRecord.batch_id] });
      const previousRecords = queryClient.getQueryData<DailyRecord[]>(['cbs', 'dailyRecords', newRecord.batch_id]);

      const optimisticRecord: DailyRecord = {
        ...newRecord,
        id: `temp-${Date.now()}`,
        running_bird_count: null,
        running_cumulative_feed_kg: null,
        running_fcr: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      queryClient.setQueryData<DailyRecord[]>(['cbs', 'dailyRecords', newRecord.batch_id], (old = []) => [
        optimisticRecord,
        ...old,
      ]);
      addRecord(optimisticRecord);

      if (activeBatch && activeBatch.id === newRecord.batch_id) {
        updateBatch(activeBatch.id, {
          current_bird_count: (activeBatch.current_bird_count ?? activeBatch.doc_count) - newRecord.mortality_count,
          cumulative_mortality: (activeBatch.cumulative_mortality ?? 0) + newRecord.mortality_count,
          cumulative_feed_kg: (activeBatch.cumulative_feed_kg ?? 0) + (newRecord.feed_consumed_kg ?? 0),
        });
      }

      return { previousRecords };
    },

    onError: (error, variables, context) => {
      if (context?.previousRecords) {
        queryClient.setQueryData(['cbs', 'dailyRecords', variables.batch_id], context.previousRecords);
      }
      toast({ title: i18next.t("cbs.dailyRecord.saveFailed", { defaultValue: "Failed to save record" }), description: error instanceof Error ? error.message : i18next.t("common.unknownError", { defaultValue: "Unknown error" }), variant: 'destructive' });
    },

    onSuccess: () => {
      toast({ title: i18next.t("cbs.dailyRecord.dayLogged", { defaultValue: "Day logged!" }), description: i18next.t("cbs.dailyRecord.saveSuccess", { defaultValue: "Record saved successfully" }) });
    },

    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({ queryKey: ['cbs', 'dailyRecords', variables.batch_id] });
      queryClient.invalidateQueries({ queryKey: ['cbs', 'batch', 'active'] });
      queryClient.invalidateQueries({ queryKey: ['cbs', 'dailyRecord', 'today'] });
      queryClient.invalidateQueries({ queryKey: ['cbs', 'today-task-status'] });
    },
  });
};

// Update daily record
export const useUpdateDailyRecord = () => {
  const queryClient = useQueryClient();
  const { updateRecord } = useCBSDailyRecordStore();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<DailyRecord> }) => {
      const client = getAuthenticatedClient();
      const { error } = await client
        .from('daily_records')
        .update(data)
        .eq('id', id);
      if (error) throw error;
    },

    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: ['cbs', 'dailyRecords'] });
      updateRecord(id, data);
      return {};
    },

    onError: (error) => {
      toast({ title: i18next.t("cbs.dailyRecord.updateFailed", { defaultValue: "Failed to update record" }), description: error instanceof Error ? error.message : i18next.t("common.unknownError", { defaultValue: "Unknown error" }), variant: 'destructive' });
    },

    onSuccess: () => {
      toast({ title: i18next.t("cbs.dailyRecord.updated", { defaultValue: "Record updated" }), description: i18next.t("common.changesSaved", { defaultValue: "Your changes have been saved" }) });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['cbs', 'dailyRecords'] });
      queryClient.invalidateQueries({ queryKey: ['cbs', 'dailyRecord', 'today'] });
      queryClient.invalidateQueries({ queryKey: ['cbs', 'today-task-status'] });
    },
  });
};